import { Link, NavLink, useNavigate } from "react-router-dom";
import { Map, LogOut, LayoutDashboard, ShieldCheck, Sun, Moon, Menu, Settings } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const TierPill = ({ user, settings }) => {
  if (!user) return null;
  const free = settings?.site_mode === "free";
  const premium = user.effective_tier === "premium";
  const label = free ? "Free Mode" : premium ? (user.subscription?.status === "trial" ? "Trial" : "Premium") : "Free";
  const cls = premium || free ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300" : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
  return <span data-testid="nav-tier-pill" className={`hidden sm:inline-flex px-2.5 py-1 rounded-full text-xs font-semibold font-mono uppercase tracking-wider ${cls}`}>{label}</span>;
};

export function Navbar() {
  const { user, settings, logout } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("cgland_theme") === "dark");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("cgland_theme", dark ? "dark" : "light");
  }, [dark]);

  const linkCls = ({ isActive }) =>
    `px-3 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? "bg-emerald-900 text-white dark:bg-emerald-500 dark:text-slate-950" : "text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"}`;

  return (
    <header className="sticky top-0 z-50 glass border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-2.5 group">
          <span className="w-9 h-9 rounded-xl bg-emerald-900 dark:bg-emerald-500 grid place-items-center text-white dark:text-slate-950 transition-transform group-hover:rotate-6">
            <Map size={18} />
          </span>
          <span className="font-extrabold tracking-tight text-lg leading-none">CG Land<span className="text-emerald-700 dark:text-emerald-400"> Geoportal</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {user && <NavLink to="/dashboard" data-testid="nav-dashboard-link" className={linkCls}>Dashboard</NavLink>}
          <NavLink to="/map" data-testid="nav-map-link" className={linkCls}>Maps</NavLink>
          <NavLink to="/pricing" data-testid="nav-pricing-link" className={linkCls}>Pricing</NavLink>
          {user?.role === "superadmin" && <NavLink to="/admin" data-testid="nav-admin-link" className={linkCls}>Admin</NavLink>}
        </nav>

        <div className="flex items-center gap-2">
          <TierPill user={user} settings={settings} />
          <Button data-testid="theme-toggle-button" variant="ghost" size="icon" className="rounded-full" onClick={() => setDark((d) => !d)} aria-label="Toggle theme">
            {dark ? <Sun size={18} /> : <Moon size={18} />}
          </Button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu-button" className="w-9 h-9 rounded-full text-white font-bold grid place-items-center overflow-hidden" style={{ background: "var(--terracotta)" }}>
                  {user.picture ? <img src={user.picture} alt={user.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" /> : user.name?.[0]?.toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 text-sm"><p className="font-semibold truncate">{user.name}</p><p className="text-xs text-muted-foreground truncate">{user.email}</p></div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-dashboard-item" onClick={() => navigate("/dashboard")}><LayoutDashboard size={16} className="mr-2" />Dashboard</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-map-item" onClick={() => navigate("/map")}><Map size={16} className="mr-2" />Maps</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-account-item" onClick={() => navigate("/account")}><Settings size={16} className="mr-2" />Account settings</DropdownMenuItem>
                {user.role === "superadmin" && <DropdownMenuItem data-testid="menu-admin-item" onClick={() => navigate("/admin")}><ShieldCheck size={16} className="mr-2" />Superadmin</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-logout-button" onClick={() => { logout(); navigate("/"); }}><LogOut size={16} className="mr-2" />Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : user === false ? (
            <>
              <Link to="/login" data-testid="nav-login-link" className="hidden sm:inline-flex text-sm font-medium px-3 py-2 hover:text-emerald-800 dark:hover:text-emerald-300">Log in</Link>
              <Button asChild data-testid="nav-register-link" className="rounded-full bg-emerald-900 hover:bg-emerald-800 dark:bg-emerald-500 dark:text-slate-950"><Link to="/register">Get started</Link></Button>
            </>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden rounded-full" data-testid="nav-mobile-menu-button"><Menu size={18} /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {user && <DropdownMenuItem onClick={() => navigate("/dashboard")}>Dashboard</DropdownMenuItem>}
              <DropdownMenuItem onClick={() => navigate("/map")}>Maps</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/pricing")}>Pricing</DropdownMenuItem>
              {user?.role === "superadmin" && <DropdownMenuItem onClick={() => navigate("/admin")}>Admin</DropdownMenuItem>}
              {!user && <DropdownMenuItem onClick={() => navigate("/login")}>Log in</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
